import { useEffect, useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { messaging } from "./config.js";
import { requestNotificationPermission, getFCMToken, onForegroundMessage, showNotification } from "./messaging.js";
import type { NotificationPayload } from "./messaging.js";
import { api } from "~/common/utils/trpc.js";

interface UseFirebaseMessagingReturn
{
  token: string | null;
  permission: NotificationPermission;
  isLoading: boolean;
  error: Error | null;
  requestPermission: () => Promise<void>;
}

// Token caching helpers
const FCM_TOKEN_KEY = "fcm_token";
const FCM_TOKEN_TIMESTAMP_KEY = "fcm_token_timestamp";
const TOKEN_REFRESH_INTERVAL = 60 * 24 * 60 * 60 * 1000; // 60 days in milliseconds

const getCachedToken = (): string | null =>
{
  try
  {
    return localStorage.getItem(FCM_TOKEN_KEY);
  }
  catch
  {
    return null;
  }
};

const setCachedToken = (token: string) =>
{
  try
  {
    localStorage.setItem(FCM_TOKEN_KEY, token);
    localStorage.setItem(FCM_TOKEN_TIMESTAMP_KEY, Date.now().toString());
  }
  catch (error)
  {
    console.error("Failed to cache FCM token:", error);
  }
};

const clearCachedToken = () =>
{
  try
  {
    localStorage.removeItem(FCM_TOKEN_KEY);
    localStorage.removeItem(FCM_TOKEN_TIMESTAMP_KEY);
  }
  catch (error)
  {
    console.error("Failed to clear cached FCM token:", error);
  }
};

const isTokenExpired = (): boolean =>
{
  try
  {
    const timestamp = localStorage.getItem(FCM_TOKEN_TIMESTAMP_KEY);
    if (!timestamp) return true;

    const age = Date.now() - parseInt(timestamp, 10);
    return age > TOKEN_REFRESH_INTERVAL;
  }
  catch
  {
    return true;
  }
};

export interface UseFirebaseMessagingOptions
{
  isLoggedIn: boolean;
}

/**
 * Hook to manage Firebase Cloud Messaging. FCM token is only synced to the backend when isLoggedIn is true.
 */
export const useFirebaseMessaging = (options: UseFirebaseMessagingOptions): UseFirebaseMessagingReturn =>
{
  const { isLoggedIn } = options;

  const [token, setToken] = useState<string | null>(() => getCachedToken());
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const isInitializedRef = useRef(false);

  const saveFcmTokenMutationOptions = api.user.saveFcmToken.mutationOptions();
  const saveFcmTokenMutation = useMutation(saveFcmTokenMutationOptions);

  const requestPermission = async () =>
  {
    try
    {
      setIsLoading(true);
      const newPermission = await requestNotificationPermission();
      setPermission(newPermission);

      if (newPermission === "granted")
      {
        clearCachedToken();

        const resolvedMessaging = await messaging;
        const fcmToken = await getFCMToken(resolvedMessaging);

        if (fcmToken)
        {
          setToken(fcmToken);
          setCachedToken(fcmToken);

          if (isLoggedIn)
          {
            try
            {
              await saveFcmTokenMutation.mutateAsync({ fcmToken });
              console.log("FCM token saved successfully");
            }
            catch (saveError)
            {
              console.error("Failed to save FCM token:", saveError);
              throw saveError;
            }
          }
        }
        else
        {
          throw new Error("Failed to get FCM token");
        }
      }
    }
    catch (err)
    {
      console.error("Error requesting notification permission:", err);
      setError(err instanceof Error ? err : new Error("Unknown error"));
    }
    finally
    {
      setIsLoading(false);
    }
  };

  useEffect(() =>
  {
    const handleFcmTokenInvalid = () =>
    {
      clearCachedToken();
      setToken(null);
    };
    window.addEventListener("fcm-token-invalid", handleFcmTokenInvalid);
    return () => window.removeEventListener("fcm-token-invalid", handleFcmTokenInvalid);
  }, []);

  useEffect(() =>
  {
    if (!isLoggedIn)
    {
      setIsLoading(false);
      return;
    }
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    setIsLoading(false);

    let isMounted = true;
    let unsubscribe: (() => void) | undefined;

    const initializeMessaging = async () =>
    {
      try
      {
        if (permission === "granted")
        {
          const cachedToken = getCachedToken();
          const tokenExpired = isTokenExpired();

          if (cachedToken && !tokenExpired)
          {
            setToken(cachedToken);
            try
            {
              await saveFcmTokenMutation.mutateAsync({ fcmToken: cachedToken });
            }
            catch (saveError)
            {
              console.error("Failed to sync token with backend:", saveError);
            }
          }
          else
          {
            const resolvedMessaging = await messaging;
            const fcmToken = await getFCMToken(resolvedMessaging);

            if (fcmToken)
            {
              setToken(fcmToken);
              setCachedToken(fcmToken);
              try
              {
                await saveFcmTokenMutation.mutateAsync({ fcmToken });
                console.log("FCM token saved to backend");
              }
              catch (saveError)
              {
                console.error("Failed to save FCM token:", saveError);
              }
            }
          }
        }

        const resolvedMessaging = await messaging;

        if (resolvedMessaging && isMounted)
        {
          unsubscribe = onForegroundMessage(resolvedMessaging, (payload: NotificationPayload) =>
          {
            if (document.visibilityState === "hidden" || !document.hasFocus())
            {
              return;
            }
            showNotification(payload);
          });
        }
      }
      catch (err)
      {
        console.error("Error initializing FCM:", err);
        if (isMounted)
        {
          setError(err instanceof Error ? err : new Error("Unknown error"));
        }
      }
    };

    initializeMessaging();

    return () =>
    {
      isMounted = false;
      if (unsubscribe)
      {
        unsubscribe();
      }
    };
  }, [isLoggedIn]);

  return {
    token,
    permission,
    isLoading,
    error,
    requestPermission,
  };
};
